import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { css } from '@compiled/react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { TriggerProps } from '@atlaskit/popup/types';
import { updatePageRovoParams } from '@atlaskit/rovo-triggers';

import { useIsAnyRemixing } from '@atlassian/malleable-ui-components/state/machine';
import { releaseRemixHandoffHolds } from '@atlassian/malleable-ui-components/state/machine/remix-handoff-hold';

import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';
import { UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { LoadableLazy } from '@confluence/loadable';
import type {
	ObjectSidebarRemixLifecycleAdapter,
	ObjectSidebarRemixLifecycleEndReason,
	RetainedObjectSidebarRemixLifecycle,
} from '@confluence/malleable-ui-common/entry-points/ObjectSidebarRemixLifecycle.ts';
import {
	admitAndRetainObjectSidebarRemixLifecycle,
	useObjectSidebarRemixLifecycleOwner,
} from '@confluence/malleable-ui-common/entry-points/ObjectSidebarRemixLifecycle.ts';
import { useObjectSidebarRemixLifecycleDiagnostics } from '@confluence/malleable-ui-common/entry-points/useObjectSidebarRemixLifecycleDiagnostics.ts';
import { subscribeToObjectSidebarRemixPickerClose } from '@confluence/malleable-ui-common/entry-points/objectSidebarRemixPickerClose.ts';
import type {
	PageRemixCloseOptions,
	PageRemixLifecycleAdmissionOutcome,
} from '@confluence/malleable-ui-common/entry-points/PageRemixContainer.ts';
import { usePageContentId } from '@confluence/page-context';
import { useRightSidebarContext } from '@confluence/page-layout-context';

const remixObjectPagePopupLoader = () =>
	import(/* webpackChunkName: "loadable-RemixObjectPagePopup" */ './RemixObjectPagePopup.tsx');

const RemixObjectPagePopup = LoadableLazy({
	loader: async () => (await remixObjectPagePopupLoader()).RemixObjectPagePopup,
});

const stablePopupTriggerStyle = css({
	position: 'absolute',
	width: 1,
	height: 1,
	overflow: 'hidden',
	pointerEvents: 'none',
});

const POPUP_TRIGGER_TEST_ID = 'object-sidebar-remix-popup-trigger';

// Covers the Remix button, matching the legacy overlay trigger, so `left-end` lines the popup up
// with the button rather than with wherever this provider renders.
const anchoredPopupTriggerStyle = css({
	position: 'absolute',
	top: 0,
	left: 0,
	width: '100%',
	height: '100%',
	pointerEvents: 'none',
});

export type ObjectSidebarRemixLifecycleAdmissionRequest = {
	conversationId?: string;
};

type RetainedObjectSidebarRemixLifecycleSession = {
	token: symbol;
	lifecycle: RetainedObjectSidebarRemixLifecycle;
};

export type ObjectSidebarRemixLifecycleAdmissionOutcome = 'retained' | 'legacy' | 'rejected';

type ObjectSidebarRemixPopupSession = {
	adfDocument: DocNode;
	loadAdfDocument: () => Promise<DocNode | null>;
	isModalOpen: boolean;
	lifecycleToken: symbol | undefined;
	sessionToken: symbol;
	/** True from a preserving close until any terminal close (including an already-settled one). */
	isTransferPending: boolean;
};

type ObjectSidebarRemixLifecycleSessionContextValue = {
	isObjectSidebarRemixModalOpen: boolean;
	/**
	 * A Remix flow is active somewhere on the page (renderer, editor, or this sidebar), so new
	 * starts are unavailable. Never feed this into Stop, dismiss, or recovery.
	 */
	isRemixStartBlockedByActiveFlow: boolean;
	retainedLifecycleToken: symbol | undefined;
	admitLifecycle: (request?: ObjectSidebarRemixLifecycleAdmissionRequest) => boolean;
	admitObjectSidebarRemixLifecycle: (
		request?: ObjectSidebarRemixLifecycleAdmissionRequest,
	) => ObjectSidebarRemixLifecycleAdmissionOutcome;
	admitRetainedObjectSidebarRemixLifecycle: (
		request?: ObjectSidebarRemixLifecycleAdmissionRequest,
	) => PageRemixLifecycleAdmissionOutcome;
	openObjectSidebarRemixModal: (
		adfDocument: DocNode,
		loadAdfDocument: () => Promise<DocNode | null>,
	) => void;
	releaseCurrentRetainedLifecycle: (reason: ObjectSidebarRemixLifecycleEndReason) => void;
	releaseRetainedLifecycleForToken: (
		expectedToken: symbol | undefined,
		reason: ObjectSidebarRemixLifecycleEndReason,
	) => void;
	releaseRetainedLifecycleWithoutEnd: (expectedToken: symbol | undefined) => void;
	closeObjectSidebarRemixModal: (options?: PageRemixCloseOptions) => void;
	stopObjectSidebarRemixThinking: (options?: { reopenModal?: boolean }) => void;
	/**
	 * Registers the element the popup should anchor to (the Remix button). Pass `null` when it
	 * unmounts; the popup then falls back to an anchor inside this provider so it stays mounted.
	 */
	registerPopupAnchor: (element: HTMLElement | null) => void;
};

const ignorePopupAnchor = () => {};

const legacySession: ObjectSidebarRemixLifecycleSessionContextValue = {
	isObjectSidebarRemixModalOpen: false,
	isRemixStartBlockedByActiveFlow: false,
	retainedLifecycleToken: undefined,
	admitLifecycle: () => true,
	admitObjectSidebarRemixLifecycle: () => 'legacy',
	admitRetainedObjectSidebarRemixLifecycle: () => ({ status: 'legacy' }),
	openObjectSidebarRemixModal: () => {},
	releaseCurrentRetainedLifecycle: () => {},
	releaseRetainedLifecycleForToken: () => {},
	releaseRetainedLifecycleWithoutEnd: () => {},
	closeObjectSidebarRemixModal: () => {},
	stopObjectSidebarRemixThinking: () => {},
	registerPopupAnchor: ignorePopupAnchor,
};

/**
 * The page the last mounted Object Sidebar session saw. Module scope, so it survives the remount
 * that navigating to another host tree causes (a folder, space overview or database, or straight
 * into editing another page).
 */
let lastSeenPageContentId: string | undefined;

const ObjectSidebarRemixLifecycleSessionContext =
	createContext<ObjectSidebarRemixLifecycleSessionContextValue>(legacySession);

export const ObjectSidebarRemixLifecycleSessionProvider = ({
	children,
	lifecycleAdapter,
}: {
	children: React.ReactNode;
	lifecycleAdapter?: ObjectSidebarRemixLifecycleAdapter;
}): React.JSX.Element => {
	const rightSidebarContext = useRightSidebarContext();
	const { width: rightSidebarWidth } = rightSidebarContext.getCurrent() || {};
	const { openConversationAssistant, isConversationAssistantSidebarActive } =
		useToggleConversationAssistant();
	const lifecycleOwner = useObjectSidebarRemixLifecycleOwner();
	const retainedLifecycleRef = useRef<RetainedObjectSidebarRemixLifecycleSession | undefined>(
		undefined,
	);
	const [retainedLifecycleToken, setRetainedLifecycleToken] = useState<symbol | undefined>();
	// A handed-off generation belongs to the page it started on, so stop holding Remix for it once an
	// Object Sidebar is on another page. Not on unmount: switching between view and edit mounts a
	// new Object Sidebar tree for the same page, and the watcher and timers still end the hold.
	// An unknown id (e.g. mid route change) is skipped, so it neither releases nor hides a change.
	const [pageContentId] = usePageContentId();
	useEffect(() => {
		if (pageContentId === undefined) {
			return;
		}
		if (lastSeenPageContentId !== undefined && lastSeenPageContentId !== pageContentId) {
			releaseRemixHandoffHolds('object-sidebar', 'left-page');
		}
		lastSeenPageContentId = pageContentId;
	}, [pageContentId]);
	const isRemixStartBlockedByActiveFlow = useIsAnyRemixing();
	const [activePopupSession, setActivePopupSession] = useState<
		ObjectSidebarRemixPopupSession | undefined
	>();
	const activePopupSessionRef = useRef<ObjectSidebarRemixPopupSession | undefined>(undefined);
	const commitActivePopupSession = useCallback(
		(popupSession: ObjectSidebarRemixPopupSession | undefined) => {
			activePopupSessionRef.current = popupSession;
			setActivePopupSession(popupSession);
		},
		[],
	);
	const isConversationAssistantSidebarActiveRef = useRef(isConversationAssistantSidebarActive);
	isConversationAssistantSidebarActiveRef.current = isConversationAssistantSidebarActive;
	const reportLifecycleDiagnostic = useObjectSidebarRemixLifecycleDiagnostics();
	const logOwnerDestroyedWithActiveFlow = useCallback(
		(transferPending: boolean) => {
			reportLifecycleDiagnostic('observed', {
				operation: 'ownerDestroyedWithActiveFlow',
				terminalReason: 'cancelled',
				...(transferPending ? { transferPending: true as const } : {}),
			});
		},
		[reportLifecycleDiagnostic],
	);
	const logOwnerDestroyedWithActiveFlowRef = useRef(logOwnerDestroyedWithActiveFlow);
	logOwnerDestroyedWithActiveFlowRef.current = logOwnerDestroyedWithActiveFlow;
	const logDuplicateTerminalAttempt = useCallback(
		(reason: ObjectSidebarRemixLifecycleEndReason) => {
			reportLifecycleDiagnostic('observed', {
				operation: 'duplicateTerminalAttempt',
				terminalReason: reason,
			});
		},
		[reportLifecycleDiagnostic],
	);

	const releaseCurrentRetainedLifecycle = useCallback(
		(reason: ObjectSidebarRemixLifecycleEndReason) => {
			const retainedLifecycle = retainedLifecycleRef.current;
			if (!retainedLifecycle) {
				return;
			}
			retainedLifecycleRef.current = undefined;
			setRetainedLifecycleToken(undefined);
			retainedLifecycle.lifecycle.settleTerminal();
			retainedLifecycle.lifecycle.end(reason);
		},
		[],
	);

	const releaseRetainedLifecycleForToken = useCallback(
		(expectedToken: symbol | undefined, reason: ObjectSidebarRemixLifecycleEndReason) => {
			const retainedLifecycle = retainedLifecycleRef.current;
			if (!expectedToken) {
				return;
			}
			if (!retainedLifecycle || retainedLifecycle.token !== expectedToken) {
				logDuplicateTerminalAttempt(reason);
				return;
			}
			retainedLifecycleRef.current = undefined;
			setRetainedLifecycleToken(undefined);
			retainedLifecycle.lifecycle.settleTerminal();
			retainedLifecycle.lifecycle.end(reason);
		},
		[logDuplicateTerminalAttempt],
	);

	const releaseRetainedLifecycleWithoutEnd = useCallback((expectedToken: symbol | undefined) => {
		const retainedLifecycle = retainedLifecycleRef.current;
		if (!expectedToken || !retainedLifecycle || retainedLifecycle.token !== expectedToken) {
			return;
		}
		// The owner already released on the accepted INSERTED; only the session's token remains.
		retainedLifecycleRef.current = undefined;
		setRetainedLifecycleToken(undefined);
	}, []);

	const admitObjectSidebarRemixLifecycle = useCallback(
		(request: ObjectSidebarRemixLifecycleAdmissionRequest = {}) => {
			// Without a lifecycle adapter, admission falls through to the legacy path.
			const lifecycleRetention = admitAndRetainObjectSidebarRemixLifecycle({
				adapter: lifecycleAdapter,
				owner: lifecycleOwner,
				request: {
					kind: 'create',
					...(request.conversationId !== undefined && { conversationId: request.conversationId }),
				},
			});
			if (
				lifecycleRetention.status === 'active-flow-rejected' ||
				lifecycleRetention.status === 'infrastructure-failure'
			) {
				return 'rejected';
			}
			if (lifecycleRetention.status === 'retained') {
				const token = Symbol('object-sidebar-remix-lifecycle');
				retainedLifecycleRef.current = {
					token,
					lifecycle: lifecycleRetention.lifecycle,
				};
				setRetainedLifecycleToken(token);
				const popupSession = activePopupSessionRef.current;
				if (popupSession) {
					commitActivePopupSession({ ...popupSession, lifecycleToken: token });
				}
				return 'retained';
			}
			return 'legacy';
		},
		[commitActivePopupSession, lifecycleAdapter, lifecycleOwner],
	);

	const admitRetainedObjectSidebarRemixLifecycle = useCallback(
		(
			request: ObjectSidebarRemixLifecycleAdmissionRequest = {},
		): PageRemixLifecycleAdmissionOutcome => {
			const status = admitObjectSidebarRemixLifecycle(request);
			if (status !== 'retained') {
				return { status };
			}
			const retained = retainedLifecycleRef.current;
			if (!retained?.lifecycle.conversationId) {
				return { status: 'rejected' };
			}
			const { token, lifecycle } = retained;
			return {
				status,
				conversationId: lifecycle.conversationId,
				lifecycle,
				observationToken: token,
				remixId: lifecycle.remixId,
				transition: (event) => lifecycle.transition(event),
				isCurrent: () =>
					retainedLifecycleRef.current?.token === token && (lifecycle.isCurrent?.() ?? true),
			};
		},
		[admitObjectSidebarRemixLifecycle],
	);

	const admitLifecycle = useCallback(
		(request: ObjectSidebarRemixLifecycleAdmissionRequest = {}) =>
			admitObjectSidebarRemixLifecycle(request) !== 'rejected',
		[admitObjectSidebarRemixLifecycle],
	);

	const openObjectSidebarRemixModal = useCallback(
		(adfDocument: DocNode, loadAdfDocument: () => Promise<DocNode | null>) => {
			commitActivePopupSession({
				adfDocument,
				loadAdfDocument,
				isModalOpen: true,
				lifecycleToken: retainedLifecycleRef.current?.token,
				sessionToken: Symbol('object-sidebar-remix-popup-session'),
				isTransferPending: false,
			});
		},
		[commitActivePopupSession],
	);

	const closeObjectSidebarRemixModal = useCallback(
		(options?: PageRemixCloseOptions) => {
			const popupSession = activePopupSessionRef.current;
			if (!popupSession) {
				return;
			}
			commitActivePopupSession({
				...popupSession,
				isModalOpen: false,
				isTransferPending: Boolean(options?.preserveRemixLifecycle),
			});
			if (options?.preserveRemixLifecycle) {
				return;
			}
			if (options?.remixLifecycleAlreadySettled) {
				releaseRetainedLifecycleWithoutEnd(popupSession.lifecycleToken);
				return;
			}
			const reason = options?.remixLifecycleEndReason ?? 'cancelled';
			if (popupSession.lifecycleToken) {
				releaseRetainedLifecycleForToken(popupSession.lifecycleToken, reason);
			} else {
				releaseCurrentRetainedLifecycle(reason);
			}
		},
		[
			commitActivePopupSession,
			releaseCurrentRetainedLifecycle,
			releaseRetainedLifecycleForToken,
			releaseRetainedLifecycleWithoutEnd,
		],
	);
	useEffect(() => {
		if (
			!pageContentId ||
			!activePopupSession?.isModalOpen ||
			isRemixStartBlockedByActiveFlow ||
			!UNSAFE_expValEqualsNoExposure(
				'cc-ce-maui-close-object-sidebar-on-selection-remix',
				'isEnabled',
				true,
			)
		) {
			return;
		}
		return subscribeToObjectSidebarRemixPickerClose((requestedContentId: string) => {
			if (requestedContentId === pageContentId) {
				closeObjectSidebarRemixModal();
			}
		});
	}, [
		activePopupSession?.isModalOpen,
		closeObjectSidebarRemixModal,
		isRemixStartBlockedByActiveFlow,
		pageContentId,
	]);

	const closeObjectSidebarRemixModalForSession = useCallback(
		(expectedSessionToken: symbol, options?: PageRemixCloseOptions) => {
			const popupSession = activePopupSessionRef.current;
			if (!popupSession || popupSession.sessionToken !== expectedSessionToken) {
				if (options?.remixLifecycleEndReason) {
					logDuplicateTerminalAttempt(options.remixLifecycleEndReason);
				}
				return;
			}
			commitActivePopupSession({
				...popupSession,
				isModalOpen: false,
				isTransferPending: Boolean(options?.preserveRemixLifecycle),
			});
			if (options?.preserveRemixLifecycle) {
				return;
			}
			if (options?.remixLifecycleAlreadySettled) {
				releaseRetainedLifecycleWithoutEnd(popupSession.lifecycleToken);
				return;
			}
			const reason = options?.remixLifecycleEndReason ?? 'cancelled';
			if (popupSession.lifecycleToken) {
				releaseRetainedLifecycleForToken(popupSession.lifecycleToken, reason);
			} else {
				releaseCurrentRetainedLifecycle(reason);
			}
		},
		[
			commitActivePopupSession,
			logDuplicateTerminalAttempt,
			releaseCurrentRetainedLifecycle,
			releaseRetainedLifecycleForToken,
			releaseRetainedLifecycleWithoutEnd,
		],
	);

	const stopObjectSidebarRemixThinking = useCallback(
		(options?: { reopenModal?: boolean }) => {
			if (!options?.reopenModal) {
				return;
			}
			const popupSession = activePopupSessionRef.current;
			if (popupSession) {
				commitActivePopupSession({ ...popupSession, isModalOpen: true });
			}
		},
		[commitActivePopupSession],
	);

	const stopObjectSidebarRemixThinkingForSession = useCallback(
		(expectedSessionToken: symbol, options?: { reopenModal?: boolean }) => {
			if (!options?.reopenModal) {
				return;
			}
			const popupSession = activePopupSessionRef.current;
			if (popupSession?.sessionToken === expectedSessionToken) {
				commitActivePopupSession({ ...popupSession, isModalOpen: true });
			}
		},
		[commitActivePopupSession],
	);

	const handleOpenSidebarConversation = useCallback(
		(conversationId: string) => {
			updatePageRovoParams({ pathway: 'chat', conversationId });
			if (!isConversationAssistantSidebarActiveRef.current) {
				openConversationAssistant('sidebar');
			}
		},
		[openConversationAssistant],
	);

	const [popupAnchorElement, setPopupAnchorElement] = useState<HTMLElement | null>(null);
	const renderPopupTrigger = useCallback(
		({ ref, ...triggerProps }: TriggerProps) => {
			if (popupAnchorElement) {
				return createPortal(
					<span
						{...triggerProps}
						ref={ref}
						css={anchoredPopupTriggerStyle}
						aria-hidden="true"
						data-testid={POPUP_TRIGGER_TEST_ID}
					/>,
					popupAnchorElement,
				);
			}
			return (
				<span
					{...triggerProps}
					ref={ref}
					css={stablePopupTriggerStyle}
					aria-hidden="true"
					data-testid={POPUP_TRIGGER_TEST_ID}
				/>
			);
		},
		[popupAnchorElement],
	);

	useEffect(() => {
		return () => {
			const retainedLifecycle = retainedLifecycleRef.current;
			if (!retainedLifecycle) {
				return;
			}
			retainedLifecycleRef.current = undefined;
			logOwnerDestroyedWithActiveFlowRef.current(
				activePopupSessionRef.current?.isTransferPending ?? false,
			);
			retainedLifecycle.lifecycle.end('cancelled');
		};
	}, []);

	const value = useMemo(
		() => ({
			isObjectSidebarRemixModalOpen: activePopupSession?.isModalOpen ?? false,
			isRemixStartBlockedByActiveFlow,
			retainedLifecycleToken,
			admitLifecycle,
			admitObjectSidebarRemixLifecycle,
			admitRetainedObjectSidebarRemixLifecycle,
			openObjectSidebarRemixModal,
			releaseCurrentRetainedLifecycle,
			releaseRetainedLifecycleForToken,
			releaseRetainedLifecycleWithoutEnd,
			closeObjectSidebarRemixModal,
			stopObjectSidebarRemixThinking,
			registerPopupAnchor: setPopupAnchorElement,
		}),
		[
			admitLifecycle,
			admitObjectSidebarRemixLifecycle,
			admitRetainedObjectSidebarRemixLifecycle,
			activePopupSession?.isModalOpen,
			closeObjectSidebarRemixModal,
			isRemixStartBlockedByActiveFlow,
			openObjectSidebarRemixModal,
			releaseCurrentRetainedLifecycle,
			releaseRetainedLifecycleForToken,
			releaseRetainedLifecycleWithoutEnd,
			retainedLifecycleToken,
			stopObjectSidebarRemixThinking,
		],
	);

	return (
		<ObjectSidebarRemixLifecycleSessionContext.Provider value={value}>
			{children}
			{activePopupSession ? (
				<RemixObjectPagePopup
					adfDocument={activePopupSession.adfDocument}
					loadAdfDocument={activePopupSession.loadAdfDocument}
					isModalOpen={activePopupSession.isModalOpen}
					isRemixStartBlocked={isRemixStartBlockedByActiveFlow}
					onClose={(options: PageRemixCloseOptions | undefined) =>
						closeObjectSidebarRemixModalForSession(activePopupSession.sessionToken, options)
					}
					onStopThinking={(options: { reopenModal?: boolean } | undefined) =>
						stopObjectSidebarRemixThinkingForSession(activePopupSession.sessionToken, options)
					}
					popupRepositionKey={rightSidebarWidth}
					onOpenSidebarConversation={handleOpenSidebarConversation}
					onAdmitRetainedRemixLifecycle={admitRetainedObjectSidebarRemixLifecycle}
					shouldWaitForRemixHandoffBeforeClose={activePopupSession.lifecycleToken !== undefined}
					trigger={renderPopupTrigger}
				/>
			) : null}
		</ObjectSidebarRemixLifecycleSessionContext.Provider>
	);
};

export const useObjectSidebarRemixLifecycleSession =
	(): ObjectSidebarRemixLifecycleSessionContextValue =>
		useContext(ObjectSidebarRemixLifecycleSessionContext);
